import {
  Flex,
  FlexProps,
  Input,
  InputGroup,
  InputRightElement,
  VStack,
} from '@chakra-ui/react'
import React, { FocusEvent, forwardRef, Ref, useEffect, useState } from 'react'
import { useScrollingInputDropdownStyle } from '~/components/shared/componentStyles/ScrollingInputDropdown.style'
import { LoadingDots } from '~/components/shared/LoadingDots'
import { useKeyPresses } from '~/hooks/useKeyboard'
import { useUserClickedExternalElements } from '~/hooks/useUserClickedExternalElements'
import { tryGetValueOrDefault } from '~/utils/common'

import { SelectItem } from './DropdownSelect'
import { Menu } from './Menu'

/**
 * ScrollingInputDropdownProps
 * @typedef {object} ScrollingInputDropdownProps
 * @property {string} [placeholder] - The placeholder text for the input
 * @property {string} [value] - The value of the input
 * @property {boolean} [disabled] - Whether the input is disabled
 * @property {boolean} [isOpen] - Whether the dropdown is open
 * @property {number} [selectedIndexOverride] - The index of the selected item
 * @property {boolean} [autoSetValueOnSelected] - Whether the input value should be set to the selected item.  Value must be a string for this to work.
 * @property {boolean} [autoCloseOnSelected] - Whether the dropdown should close when an item is selected
 * @property {boolean} [autoCloseOnBlur] - Whether the dropdown should close when the input loses focus
 * @property {boolean} [autoOpen] - Whether the dropdown should open automatically when items are present
 * @property {ScrollingInputItem} items - The items to display in the dropdown
 * @property {(value: string) => string | void} [onValueChanged] - The function to call when the input value changes.  If a string is returned, the input value will be set to that string.
 * @property {(item: ScrollingInputItem, index: number) => void} [onSelected] - The function to call when an item is selected
 * @property {() => void} [onScrollEndReached] - The function to call when the scroll end is reached
 * @property {{ height?: string, width?: string, matchWidth?: boolean }} [menuOptions] - The options for the dropdown menu
 * @property {FlexProps} [rest] - Any additional Flex properties you want to modify for the wrapper of this component.
 */
type ScrollingInputDropdownProps<TValue> = FlexProps & {
  placeholder?: string
  value?: string
  valueIsExtension?: boolean
  disabled?: boolean
  isOpen?: boolean
  selectedIndexOverride?: number
  autoSetValueOnSelected?: boolean
  autoCloseOnSelected?: boolean
  autoCloseOnBlur?: boolean
  isLoading?: boolean
  isInvalid?: boolean
  items?: SelectItem<TValue>[]
  onFocus?: (e: FocusEvent<HTMLDivElement>) => void
  onValueChanged?: (value: string) => void
  onSelected?: (value: TValue, index: number) => string | void
  onScrollEndReached?: () => void
  onBlur?: () => void
  onMenuClose?: () => void
  menuOptions?: {
    height?: string
    width?: string
    matchWidth?: boolean
  }
  inputRef?: Ref<HTMLInputElement>
}

export const RefScrollingInputDropdown = forwardRef(
  <TValue,>(
    {
      placeholder = '',
      value: externalValue = '',
      defaultValue = undefined,
      disabled = false,
      isOpen = false,
      autoSetValueOnSelected,
      autoCloseOnSelected,
      autoCloseOnBlur = true,
      isLoading = false,
      isInvalid = false,
      items = [],
      onFocus,
      onBlur,
      onValueChanged,
      onScrollEndReached,
      onSelected,
      onMenuClose,
      ...rest
    }: ScrollingInputDropdownProps<TValue>,
    ref: Ref<HTMLInputElement>,
  ) => {
    const [inputValue, setInputValue] = useState<string>('')
    const [focused, setFocused] = useState<boolean>(false)
    const containerRef = React.useRef<HTMLDivElement>(null)
    const inputRef = React.useRef<HTMLInputElement>(null)
    const styles = useScrollingInputDropdownStyle()

    const handleScrollEndReachedInternal = () => {
      //only report scroll end reached if there are items in the menu
      if (!items || items.length <= 0) {
        return
      }

      onScrollEndReached?.()
    }

    const handleInputChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(e.target.value)
      onValueChanged?.(e.target.value)
    }

    const handleClose = () => {
      onMenuClose?.()
    }

    const handleInputFocus = (e: FocusEvent<HTMLDivElement>) => {
      setFocused(true)
      onFocus?.(e)
    }

    const handleInputFocusLost = () => {
      setFocused(false)
      onBlur?.()

      if (autoCloseOnBlur) {
        handleClose()
      }
    }

    const handleSelectedInternal = (value: TValue, index: number) => {
      const returnVal = onSelected?.(value, index)

      if (returnVal && typeof returnVal === 'string') {
        setInputValue(returnVal)
      } else {
        if (autoSetValueOnSelected && typeof value === 'string') {
          setInputValue(value)
        }
      }

      if (autoCloseOnSelected) {
        handleClose()
      }
    }

    useUserClickedExternalElements(
      [containerRef.current],
      true,
      handleInputFocusLost,
    )

    const keyPress = useKeyPresses()
    useEffect(() => {
      const shouldHandleBlur = keyPress?.key === 'Tab' && focused && isOpen
      if (shouldHandleBlur) {
        handleInputFocusLost()
      }
    }, [keyPress])

    const shouldBeOpen = isOpen && !disabled

    const derivedInputValue = defaultValue
      ? undefined
      : tryGetValueOrDefault(externalValue, inputValue)
    const disabledValue = defaultValue
      ? undefined
      : disabled
        ? ''
        : derivedInputValue

    return (
      <Flex ref={containerRef} {...rest} sx={styles.container}>
        <VStack sx={styles.vstack}>
          <InputGroup ref={inputRef}>
            <Input
              ref={ref}
              placeholder={placeholder}
              value={disabledValue}
              defaultValue={defaultValue}
              onChange={handleInputChanged}
              onFocus={handleInputFocus}
              isInvalid={isInvalid}
            />
            {isLoading && (
              <InputRightElement>
                <LoadingDots />
              </InputRightElement>
            )}
          </InputGroup>
          {shouldBeOpen && (
            <Menu
              anchorRef={inputRef}
              items={items}
              onSelect={handleSelectedInternal}
              onScrollEndReached={handleScrollEndReachedInternal}
            />
          )}
        </VStack>
      </Flex>
    )
  },
)

// Turns out getting a generic type for the ref is a little bit of a trick.
export const ScrollingInputDropdown = <T,>({
  inputRef,
  ...rest
}: ScrollingInputDropdownProps<T> & {
  inputRef?: React.Ref<HTMLInputElement>
}) => (
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error
  <RefScrollingInputDropdown {...rest} ref={inputRef} />
)
